import React from 'react';
import { ICoreContext, Image, IImageSettings, ImageFetchPriority } from '@msdyn365-commerce/core';
import { ensureBaseImageUrl } from '../../shared-utils/image-utilities';
import { ProductListProduct } from '../product-list-component/product-list-component';
import { SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { getConversionText, getKilogramConversion, getNonBreakoffUOM, getPcsConversion, getRoundedFactor, getTotalConversionText } from '../../shared-utils/unit-conversion-utils';
import PriceComponent from '../../themes/dlvry-adventureworks/views/components/price.component';
import { multiplyPriceBy } from '../../shared-utils/price-utils';
import { humanizeUnitOfMeasure } from '../unit-of-measure';
import { getProductPageUrlSync, IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { IAlternativeProductsResources } from './alternative-products';
import { Availability } from './availability';
import QuantityInput from '../common/quantity-input';
import QuantityBreakoffSelector from '../common/quantity-breakoff-selector';

type AlternativeProductLineProps = {
    id: string;
    typeName: string;
    context: ICoreContext;
    resources: IAlternativeProductsResources;
    product: ProductListProduct;
    simpleProduct?: SimpleProduct;
    unitConversions?: UnitOfMeasureConversion[];
    availability?: IProductInventoryInformation;
    onChangeProduct?: (product: ProductListProduct) => void;
};

function ProductImage({
    context,
    product
}: {
    context: ICoreContext;
    product: ProductListProduct;
}) {
    const width = 90;
    const height = 90;
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
            lg: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
            xl: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` }
        },
        disableLazyLoad: false,
        lazyload: true,
        quality: 80
    };

    const image = ensureBaseImageUrl(context, product.PrimaryImageUrl);
    const contextConfig = context.request.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
    if (fallbackImageSource?.endsWith('/')) {
        fallbackImageSource = fallbackImageSource.slice(0, -1);
    }
    if (emptyPlaceHolderImage) {
        fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
    }

    return (
        <Image
            requestContext={context.actionContext.requestContext}
            className='product-image'
            altText={product.Name}
            title={product.Name}
            src={image || fallbackImageSource}
            fallBackSrc={fallbackImageSource}
            gridSettings={context.request.gridSettings!}
            imageSettings={defaultImageSettings}
            loadFailureBehavior='empty'
            fetchPriority={ImageFetchPriority.High}
            tabIndex={0}
        />
    );
}

export function AlternativeProductLine({
    id,
    typeName,
    context,
    resources,
    product,
    simpleProduct,
    unitConversions,
    availability,
    onChangeProduct
}: AlternativeProductLineProps) {
    const className = 'dlvry-alternative-products-popup';

    const [breakoff, setBreakoff] = React.useState(false);

    const conversions = unitConversions?.filter(x => x.ProductRecordId === product.RecordId);
    const kilogramConversion = conversions && getKilogramConversion(simpleProduct?.DefaultUnitOfMeasure, conversions);
    const pcsConversion = conversions && getPcsConversion(conversions, simpleProduct?.DefaultUnitOfMeasure);

    let productPrice = product.ProductPrice ? { ...product.ProductPrice } : undefined;

    if (productPrice && !breakoff && simpleProduct?.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    const price = product.ProductPrice?.CustomerContextualPrice;
    const productUrl = product ? getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined) : '';

    const totalConversionText = getTotalConversionText(
        context,
        simpleProduct,
        conversions,
        breakoff ? 1 : simpleProduct?.Behavior?.MultipleOfQuantity || 1,
        breakoff
    );

    const additionalInformation = (product.AlcoholProduct && product.AlcoholProduct > 0 && product.ImporterGrouping) && (
        <div className={`${className}__details__additional-information importer-grouping`}>{product.ImporterGrouping}</div>
    ) || product.Brand && (
        <div className={`${className}__details__additional-information brand`}>{product.Brand}</div>
    ) || product.Manufacturer && (
        <div className={`${className}__details__additional-information manufacturer`}>{product.Manufacturer}</div>
    ) || product.Producer && (
        <div className={`${className}__details__additional-information producer`}>{product.Producer}</div>
    );

    return (
        <li key={product.RecordId} className={`${className}__list__item`} role='listitem'>
            <div className={`${className}__image`} style={{ minWidth: 50, minHeight: 50 }}>
                <a href={productUrl} title={product.Name}>
                    <ProductImage context={context} product={product} />
                </a>
            </div>
            <div className={`${className}__details`}>
                <div className={`${className}__details__name-and-price`}>
                    <div className={`${className}__details__name-wrapper`}>
                        <a className={`${className}__details__name`} href={productUrl}>
                            {product.Name}
                        </a>

                        <div className={`${className}__details__conversion-text`}>
                            {getConversionText(
                                context,
                                simpleProduct?.DefaultUnitOfMeasure,
                                conversions || [],
                                simpleProduct,
                                breakoff
                            )}
                        </div>

                        <div className={`${className}__details__id`}>
                            {product.ItemId}
                        </div>

                        {additionalInformation}
                    </div>

                    <div className={`${className}__price`}>
                        {productPrice && (
                            <PriceComponent
                                context={context}
                                id={id}
                                typeName={typeName}
                                data={{ price: productPrice }}
                            />
                        )}

                        {price && (!!(!breakoff && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1)) && (
                            <div className={`${className}__price__extra multiple`}>
                                <PriceComponent
                                    context={context}
                                    data={{ price: { CustomerContextualPrice: price } }}
                                    id={id}
                                    typeName={typeName} />
                                &nbsp;
                                pr. {humanizeUnitOfMeasure(context, product?.DefaultUnitOfMeasure).toLowerCase()}
                            </div>
                        )}

                        {price && kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                            <div className={`${className}__price__extra kilogram`}>
                                <PriceComponent
                                    context={context}
                                    data={{ price: { CustomerContextualPrice: price / getRoundedFactor(kilogramConversion) } }}
                                    id={id}
                                    typeName={typeName} />
                                &nbsp;
                                pr. kg
                            </div>
                        )}

                        {price && pcsConversion && (
                            <div className={`${className}__price__extra pcs`}>
                                <PriceComponent
                                        context={context}
                                        data={{ price: { CustomerContextualPrice: price / (pcsConversion.Factor || 1) } }}
                                        id={id}
                                        typeName={typeName} />
                                &nbsp;
                                pr. stk
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${className}__details__conversion`}>
                    {/* <div>
                        {!!isM1ProductAndHasBreakoff && `1 x ${simpleProduct!.Behavior!.MultipleOfQuantity} `}
                        {uom}
                    </div> */}
                    <QuantityBreakoffSelector
                        context={context}
                        product={simpleProduct}
                        quantity={breakoff ? 1 : simpleProduct?.Behavior?.MultipleOfQuantity || 1}
                        breakoff={breakoff}
                        unitConversions={conversions}
                        onBreakoffChange={setBreakoff}
                        onQuantityChange={() => void 0}
                    />
                    <QuantityInput
                        mode='view'
                        context={context}
                        product={simpleProduct}
                        quantity={breakoff ? 1 : simpleProduct?.Behavior?.MultipleOfQuantity || 1}
                        breakoff={breakoff}
                        showNonBreakoffAmount
                        nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, conversions)}
                        onQuantityChange={() => void 0}
                    />
                    <span className={`${className}__total-conversion-text`} style={{ minWidth: 8.22 }}>
                        {totalConversionText && '='}
                    </span>
                    <span className={`${className}__total-conversion-text`} style={{ minWidth: 60 }}>
                        {totalConversionText}
                    </span>
                </div>
                <div className={`${className}__details__purchase-info`}>
                    <Availability baseClassName={className} context={context} availability={availability} />

                    <div className={`${className}__details__purchase-info__actions`}>
                        <button
                            className={`${className}__details__purchase-info__actions__change`}
                            onClick={() => onChangeProduct?.(product)}
                        >
                            {resources.alternativeProductsChangeProductButtonText}
                        </button>
                    </div>
                </div>
            </div>
        </li>
    );
}
